import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import ProductPage from '@/models/product-page';
import ProductType from '@/models/product-type';
import OrderFlow from '@/models/order-flow';
import Product from '@/models/product';
import { getDayFromDate } from '@/utils/utilities';

@Module({ name: 'food', namespaced: true })
export default class FoodModule extends VuexModule {
    productPages: ProductPage[] = [];
    products: Product[] = [];
    orderFlows: OrderFlow[] = [];
    currentProductPage: ProductPage | null = null;
    isReady: boolean = false;
    menuModalOpen: boolean = false;
    isLoading: boolean = false;
    search: string = '';

    @Mutation
    public setProductPages(productPages: ProductPage[]): void {
        this.productPages = productPages;
    }

    @Mutation
    public setProductPage(slug: string): void {
        this.currentProductPage = this.productPages.find((productPage) => productPage.slug === slug) || null;
    }

    @Mutation
    public setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @Mutation
    public openMenuModal(): void {
        this.menuModalOpen = true;
    }

    @Mutation
    public closeMenuModal(): void {
        this.menuModalOpen = false;
    }

    @Mutation
    public setSearch(search: string): void {
        this.search = search;
    }

    @Mutation
    public setIsReady(): void {
        this.isReady = true;
    }

    @Mutation
    public setProducts(products: Product[]): void {
        this.products = products;
    }

    @Mutation
    public setOrderFlows(orderFlows: OrderFlow[]): void {
        this.orderFlows = orderFlows;
    }

    @Action({ commit: 'setOrderFlows' })
    public async fetchOrderFlows(): Promise<OrderFlow[]> {
        return OrderFlow.all();
    }

    @Action({ commit: 'setProducts' })
    public async setProductsFromProductPages() {
        const pageProducts = this.productPages.flatMap((page) => page.products);
        const typeProducts = this.productPages.flatMap((page) => page.product_types.flatMap((type) => type.products));
        const allProducts = [...pageProducts, ...typeProducts];

        const uniqueProducts = Array.from(new Set(allProducts.map((product) => product!.id))).map((id) => allProducts.find((product) => product!.id === id));

        return uniqueProducts;
    }

    @Action({ commit: 'setProductPages' })
    public async fetchProductPages(): Promise<ProductPage[]> {
        return await ProductPage.all();
    }

    public getProduct(slug: string): Product {
        const product = this.products.find((product) => product.slug === slug) as Product;
        return product;
    }

    get activeProductPages() {
        return this.productPages.filter((page) => page.product_types.length > 0 || page.products.length > 0);
    }

    get closedProductPages() {
        const now = new Date();
        return this.activeProductPages.filter((page) => page.schedule && page.schedule[getDayFromDate(now)] && page.schedule[getDayFromDate(now)].length > 0);
    }

    get searchResults() {
        if (this.search.length > 2) {
            const searchTerms = this.search
                .toLowerCase()
                .split(' ')
                .filter((term) => term.length > 2);
            let searchResults: ProductPage[] = [];
            for (let page of <ProductPage[]>[...this.activeProductPages]) {
                let matchingTypes: ProductType[] = [];
                let matchingProductsInPages: Product[] = [];

                for (let type of page.product_types) {
                    let matchingProducts: Product[] = [];
                    if (type.products?.length) {
                        for (let product of type.products) {
                            if (
                                searchTerms.every((term) => product.name.toLowerCase().includes(term)) ||
                                (product.description && searchTerms.every((term) => product.description.toLowerCase().includes(term))) ||
                                (searchTerms.some((term) => product.name.toLowerCase().includes(term)) && product.description && searchTerms.some((term) => product.description.toLowerCase().includes(term)))
                            ) {
                                matchingProducts.push(product);
                            }
                        }
                    }
                    if (matchingProducts.length) {
                        type.products = matchingProducts;
                        matchingTypes.push(type);
                    }
                }
                if (matchingTypes.length) {
                    page.product_types = matchingTypes;
                }
                if (page.products.length) {
                    for (let product of page.products) {
                        if (
                            searchTerms.every((term) => product.name.toLowerCase().includes(term)) ||
                            (product.description && searchTerms.every((term) => product.description.toLowerCase().includes(term))) ||
                            (searchTerms.some((term) => product.name.toLowerCase().includes(term)) && product.description && searchTerms.some((term) => product.description.toLowerCase().includes(term)))
                        ) {
                            matchingProductsInPages.push(product);
                        }
                    }
                    if (matchingProductsInPages.length) {
                        page.products = matchingProductsInPages;
                    }
                }
                if (searchTerms.some((term) => page.name.toLowerCase().includes(term)) || matchingProductsInPages.length || matchingTypes.length) {
                    searchResults.push(page);
                }
            }
            return searchResults;
        }
        return this.activeProductPages;
    }
}
